// src/modules/purityAnalyzer/pages/PurityAnalytics.jsx
// All analytics charts: purity/density timelines, calibration & temperature
// drift, distribution, daily tests, branch & operator comparison,
// calibration history. HO gets a branch filter; branch users see own data.

import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, Empty, Spin } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { Line, Column } from '@ant-design/charts';
import dayjs from 'dayjs';
import { fetchChart } from '../api';
import useBranchScope from '../hooks/useBranchScope';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const GOLD = '#d4a017';
const PURPLE = '#722ed1';
const BLUE = '#1677ff';

function ChartCard({ title, loading, empty, children }) {
  return (
    <Card size="small" title={title} style={{ height: '100%' }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : empty ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data yet" />
      ) : children}
    </Card>
  );
}

export default function PurityAnalytics() {
  const { isHO, branches } = useBranchScope();
  const [branchId, setBranchId] = useState();
  const [range, setRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const params = useCallback(() => ({
    branchId,
    dateFrom: range?.[0]?.startOf('day')?.toISOString(),
    dateTo: range?.[1]?.endOf('day')?.toISOString(),
  }), [branchId, range]);

  const load = useCallback(async () => {
    setLoading(true);
    const keys = [
      'purity-timeline', 'density-timeline', 'calibration-drift', 'temperature-drift',
      'purity-distribution', 'daily-tests', 'branch-comparison', 'operator-comparison',
      'calibration-history',
    ];
    try {
      const results = await Promise.allSettled(keys.map((k) => fetchChart(k, params())));
      const next = {};
      results.forEach((r, i) => {
        next[keys[i]] = r.status === 'fulfilled' ? (r.value?.data?.data || []) : [];
      });
      setData(next);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const d = (k) => data[k] || [];
  const common = { height: 240, animation: true };

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          <BarChartOutlined style={{ marginRight: 10 }} />Purity Analytics
        </Title>
        <Space wrap>
          {isHO && (
            <Select allowClear placeholder="All branches" style={{ width: 180 }}
              options={branches.map((b) => ({ value: b._id, label: b.name }))}
              onChange={setBranchId} />
          )}
          <RangePicker value={range} onChange={setRange} allowClear={false} />
        </Space>
      </Space>

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <ChartCard title="Purity Timeline" loading={loading} empty={!d('purity-timeline').length}>
            <Line {...common} data={d('purity-timeline')} xField="date" yField="avgPurity"
              shapeField="smooth" style={{ stroke: GOLD, lineWidth: 2 }}
              axis={{ y: { title: 'Avg purity %' } }} />
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Density Timeline" loading={loading} empty={!d('density-timeline').length}>
            <Line {...common} data={d('density-timeline')} xField="date" yField="avgGoldDensity"
              shapeField="smooth" style={{ stroke: PURPLE, lineWidth: 2 }}
              axis={{ y: { title: 'Avg gold density g/cm³' } }} />
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Calibration Drift (diesel density per calibration)"
            loading={loading} empty={!d('calibration-drift').length}>
            <Line {...common}
              data={d('calibration-drift').map((r) => ({ ...r, date: dayjs(r.date).format('DD MMM HH:mm') }))}
              xField="date" yField="dieselDensity" colorField="machine"
              style={{ lineWidth: 2 }}
              axis={{ y: { title: 'Diesel density g/cm³' } }} />
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Temperature Drift" loading={loading} empty={!d('temperature-drift').length}>
            <Line {...common} data={d('temperature-drift')} xField="date" yField="avgTemperatureIndex"
              shapeField="smooth" style={{ stroke: '#fa8c16', lineWidth: 2 }}
              axis={{ y: { title: 'Temp index %' } }} />
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Purity Distribution" loading={loading} empty={!d('purity-distribution').length}>
            <Column {...common}
              data={d('purity-distribution').map((r) => ({ ...r, bucket: `≥${r.bucket}%` }))}
              xField="bucket" yField="count"
              style={{ fill: GOLD, radiusTopLeft: 4, radiusTopRight: 4 }} />
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Daily Tests" loading={loading} empty={!d('daily-tests').length}>
            <Column {...common} data={d('daily-tests')} xField="date" yField="tests"
              style={{ fill: BLUE, radiusTopLeft: 4, radiusTopRight: 4 }} />
          </ChartCard>
        </Col>
        {isHO && (
          <Col xs={24} lg={12}>
            <ChartCard title="Branch Comparison" loading={loading} empty={!d('branch-comparison').length}>
              <Column {...common} data={d('branch-comparison')} xField="branch" yField="avgPurity"
                style={{ fill: GOLD, radiusTopLeft: 4, radiusTopRight: 4 }}
                axis={{ y: { title: 'Avg purity %' } }} />
            </ChartCard>
          </Col>
        )}
        <Col xs={24} lg={12}>
          <ChartCard title="Operator Comparison" loading={loading} empty={!d('operator-comparison').length}>
            <Column {...common} data={d('operator-comparison')} xField="operator" yField="tests"
              style={{ fill: PURPLE, radiusTopLeft: 4, radiusTopRight: 4 }} />
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Calibration History (per day)" loading={loading}
            empty={!d('calibration-history').length}>
            <Column {...common} data={d('calibration-history')} xField="date" yField="calibrations"
              style={{ fill: '#13c2c2', radiusTopLeft: 4, radiusTopRight: 4 }} />
          </ChartCard>
        </Col>
      </Row>
    </div>
  );
}
